import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import i18n from '../../i18n';
import type { CloudDishLibraryMenuListSluItem } from '../../request/api/rbp/cloud-dish-library';
import type { CloudDishLibraryMenuSummary } from '../../utils/cloud-dish-library';
import MenuListComponent, { type MenuListItem } from '../dish-menu-creator/menu-list/menu-list-component';
import type { MenuLibrarySubcategoryPickerMode } from './menu-library-subcategory-picker-dialog.component';
import './scss/menu-library-subcategory-picker-dialog.style.scss';

/** 云端复制/转移确认时选中的目标菜单 + 小类 */
export type CloudSubcategoryPickerTarget = {
    /** 目标云端菜单 id（`target_cloud_menu_id`） */
    target_cloud_menu_id: string;
    /** 目标云端小类 id（`target_cloud_slu_id`） */
    target_cloud_slu_id: string;
    /** 选中的小类摘要（展示/调试用） */
    slu: CloudDishLibraryMenuListSluItem;
};

export type MenuLibraryCloudSubcategoryPickerDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 转移 / 复制 */
    mode: MenuLibrarySubcategoryPickerMode | null;
    /** 关闭 */
    onClose: () => void;
    /** 云端菜单列表（含 `menu_item_slu`，来自 `GET .../menus`） */
    cloudMenus: CloudDishLibraryMenuSummary[];
    /** 列表加载中 */
    loading?: boolean;
    /** 不可选的云端小类 `_id`（如当前小类） */
    excludeCloudSluIds: string[];
    /** 确认中 */
    confirmLoading?: boolean;
    /** 确认回调 */
    onConfirm: (target: CloudSubcategoryPickerTarget) => void | Promise<void>;
};

/**
 * 云端详情批量转移/复制：跨云端菜单选择目标二级菜单（按小类 `_id` 单选）。
 * 数据源为列表 API 带回的 `menu_item_slu`，不用本地 `dmi_slu_id`。
 */
export const MenuLibraryCloudSubcategoryPickerDialog: React.FC<MenuLibraryCloudSubcategoryPickerDialogProps> = ({
    open,
    mode,
    onClose,
    cloudMenus,
    loading,
    excludeCloudSluIds,
    confirmLoading,
    onConfirm,
}) => {
    // 当前选中的云端小类 `_id`
    const [selectedCloudSluId, setSelectedCloudSluId] = useState<string | null>(null);
    // 选中目标（含所属云端菜单 id）
    const [selectedTarget, setSelectedTarget] = useState<CloudSubcategoryPickerTarget | null>(null);

    useEffect(() => {
        if (!open) {
            setSelectedCloudSluId(null);
            setSelectedTarget(null);
        }
    }, [open]);

    const checkedIds = selectedCloudSluId != null ? [selectedCloudSluId] : [];

    const excludeSet = useMemo(
        () => new Set(excludeCloudSluIds.filter((id) => id != null && id.trim() !== '')),
        [excludeCloudSluIds],
    );

    const hasAnySlu = useMemo(() => {
        for (const fm of cloudMenus) {
            for (const slu of fm.menu_item_slu || []) {
                if (slu._id && !excludeSet.has(slu._id)) {
                    return true;
                }
            }
        }
        return false;
    }, [cloudMenus, excludeSet]);

    const menuTree = useMemo(
        () =>
            cloudMenus.map((item, fmIndex) => ({
                key: `cloud-fm-${item.id}-${fmIndex}`,
                title: item.main_group_name ?? '',
                children: (item.menu_item_slu || [])
                    .filter((sub) => sub._id && !excludeSet.has(sub._id))
                    .map((subMenuItem) => ({
                        key: `cloud-sub-${subMenuItem._id}`,
                        title: subMenuItem.dmi_slu_name ?? '',
                        dataSource: { ...subMenuItem, __cloud_menu_id: item.id },
                        checkedSecondMenuIds: checkedIds,
                        originId: subMenuItem._id,
                    })),
                dataSource: item,
            })),
        [cloudMenus, excludeSet, checkedIds],
    );

    const handleClick = useCallback(
        (menuItem: MenuListItem) => {
            if (menuItem.children) {
                return;
            }
            const cloudSluId = menuItem.originId != null ? String(menuItem.originId) : '';
            if (!cloudSluId) {
                return;
            }
            const ds = menuItem.dataSource as CloudDishLibraryMenuListSluItem & { __cloud_menu_id?: string };
            const cloudMenuId = typeof ds.__cloud_menu_id === 'string' ? ds.__cloud_menu_id : '';
            if (!cloudMenuId) {
                return;
            }
            if (checkedIds.includes(cloudSluId)) {
                setSelectedCloudSluId(null);
                setSelectedTarget(null);
                return;
            }
            setSelectedCloudSluId(cloudSluId);
            setSelectedTarget({
                target_cloud_menu_id: cloudMenuId,
                target_cloud_slu_id: cloudSluId,
                slu: ds,
            });
        },
        [checkedIds],
    );

    const title =
        mode === 'transfer'
            ? i18n.t('menu_library_create_dialog_subcategory_picker_title_transfer')
            : mode === 'copy'
              ? i18n.t('menu_library_create_dialog_subcategory_picker_title_copy')
              : '';

    /** 确认选中目标小类 */
    const handleConfirm = () => {
        if (selectedTarget) {
            void onConfirm(selectedTarget);
        }
    };

    return (
        <Dialog
            open={open && mode != null}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            scroll="paper"
            className="menu-library-subcategory-picker-dialog"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: '2px solid var(--fill-primary-300)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    boxShadow: 'none',
                    overflow: 'hidden',
                },
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                },
            }}
        >
            <DialogTitle
                className="menu-library-subcategory-picker-dialog__title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    pr: 3,
                    backgroundColor: 'var(--fill-neutral-100)',
                }}
            >
                <Typography component="span" variant="h6" sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
                    {title}
                </Typography>
                <IconButton
                    type="button"
                    className="header-close-icon"
                    aria-label={i18n.t('extra_cost_text_close')}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent
                className="menu-library-subcategory-picker-dialog__body"
                dividers
                sx={{ backgroundColor: 'var(--fill-neutral-100)' }}
            >
                {loading && cloudMenus.length === 0 ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={32} />
                    </Box>
                ) : !hasAnySlu ? (
                    <Typography variant="body2" color="text.secondary">
                        {i18n.t('menu_library_create_dialog_subcategory_picker_empty')}
                    </Typography>
                ) : (
                    <Box className="menu-library-subcategory-picker-dialog__list-wrap">
                        <MenuListComponent
                            fullWidth
                            isHideContextMenu
                            menu={menuTree}
                            isShowCheckBox
                            handleClick={handleClick}
                            style={{ maxHeight: 'unset' }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                className="menu-library-subcategory-picker-dialog__actions"
                sx={{ px: 3, pb: 2, backgroundColor: 'var(--fill-neutral-100)' }}
            >
                <Button color="primary" variant="outlined" onClick={onClose} disabled={!!confirmLoading}>
                    {i18n.t('google_text_public_button_cancel')}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!selectedTarget || !!confirmLoading || !hasAnySlu}
                    startIcon={
                        confirmLoading ? (
                            <CircularProgress size={18} color="inherit" aria-hidden />
                        ) : undefined
                    }
                >
                    {i18n.t('google_text_confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
